import React, { useEffect, useState, useLayoutEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Check for reduced motion preference
const prefersReducedMotion = () => {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

interface SlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  position?: 'right' | 'left';
  showBackdrop?: boolean;
  backdropClassName?: string;
  panelClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  closeOnBackdropClick?: boolean;
  showCloseButton?: boolean;
  headerContent?: React.ReactNode;
  // For positioning below headers
  topOffset?: string;
  showEdgeCloseButton?: boolean;
  // Control over animation timing
  onAnimationComplete?: () => void;
  // Make backdrop immediately clickable
  immediateBackdrop?: boolean;
  // Custom z-index for layering control
  zIndex?: number;
}

const widthClasses = {
  sm: 'w-80',
  md: 'w-96',
  lg: 'w-[480px]',
  xl: 'w-[640px]',
  '2xl': 'w-[800px]',
  full: 'w-full'
};

export function SlidePanel({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width = 'lg',
  position = 'right',
  showBackdrop = true,
  backdropClassName,
  panelClassName,
  headerClassName,
  contentClassName,
  footerClassName,
  closeOnBackdropClick = true,
  showCloseButton = true,
  headerContent,
  topOffset = '0',
  showEdgeCloseButton = false,
  onAnimationComplete,
  immediateBackdrop = false,
  zIndex = 180
}: SlidePanelProps) {
  const [showCloseBtn, setShowCloseBtn] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Custom close handler that animates first
  const handleClose = () => {
    if (!isClosing && hasAnimated) {
      setIsClosing(true);
      setShowCloseBtn(false);
      setContentVisible(false);
      setBackdropVisible(false);

      // Wait for animation to complete, then call parent onClose
      const animationDuration = reducedMotion ? 100 : 350;
      setTimeout(() => {
        setHasAnimated(false);
        setIsClosing(false);
        onClose();
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, animationDuration);
    } else {
      // If not animated yet or already closing, just close immediately
      onClose();
    }
  };

  useEffect(() => {
    setReducedMotion(prefersReducedMotion());
  }, []);

  // Handle Escape key to close panel
  useEffect(() => {
    if (!isOpen) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleClose]);

  // Lock body scroll when panel is open (prevents horizontal scrollbar during animation)
  // Use useLayoutEffect to run synchronously BEFORE browser paint
  useLayoutEffect(() => {
    if (isOpen) {
      const body = document.body;
      const html = document.documentElement;
      const scrollY = window.scrollY;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Store original styles
      const originalPosition = body.style.position;
      const originalTop = body.style.top;
      const originalLeft = body.style.left;
      const originalRight = body.style.right;
      const originalWidth = body.style.width;
      const originalOverflow = body.style.overflow;
      const originalPaddingRight = body.style.paddingRight;
      const originalHtmlOverflow = html.style.overflow;
      const originalHtmlWidth = html.style.width;

      // CRITICAL: Lock both HTML and body to prevent ANY scrollbars
      html.style.overflow = 'hidden';
      html.style.width = '100vw';
      html.style.maxWidth = '100vw';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100vw';
      body.style.maxWidth = '100vw';
      body.style.overflow = 'hidden';
      body.style.paddingRight = `${scrollbarWidth}px`;

      return () => {
        // Restore original styles
        html.style.overflow = originalHtmlOverflow;
        html.style.width = originalHtmlWidth;
        html.style.maxWidth = '';
        body.style.position = originalPosition;
        body.style.top = originalTop;
        body.style.left = originalLeft;
        body.style.right = originalRight;
        body.style.width = originalWidth;
        body.style.maxWidth = '';
        body.style.overflow = originalOverflow;
        body.style.paddingRight = originalPaddingRight;

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Handle opening animation
  useEffect(() => {
    if (isOpen && !hasAnimated && !isClosing) {
      // Force a reflow by reading the DOM, then set hasAnimated
      requestAnimationFrame(() => {
        setHasAnimated(true);

        const backdropDelay = immediateBackdrop ? 0 : (reducedMotion ? 0 : 50);
        const closeDelay = reducedMotion ? 50 : 350;
        const contentDelay = reducedMotion ? 0 : 250;

        setTimeout(() => setBackdropVisible(true), backdropDelay);
        setTimeout(() => setShowCloseBtn(true), closeDelay);
        setTimeout(() => setContentVisible(true), contentDelay);
      });
    }
  }, [isOpen, hasAnimated, isClosing, reducedMotion, immediateBackdrop]);

  // Handle external close (when parent sets isOpen to false directly)
  // This ensures internal state is reset even when handleClose() isn't called
  useEffect(() => {
    if (!isOpen && hasAnimated && !isClosing) {
      // External close detected - animate out and reset state
      setShowCloseBtn(false);
      setContentVisible(false);
      setBackdropVisible(false);

      const animationDuration = reducedMotion ? 100 : 350;
      const timer = setTimeout(() => {
        setHasAnimated(false);
      }, animationDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, hasAnimated, isClosing, reducedMotion]);


  // Calculate transform for slide animation
  // For right-positioned panels: translateX(0) = visible, translateX(100%) = off-screen right
  // For left-positioned panels: translateX(0) = visible, translateX(-100%) = off-screen left
  const getTransform = () => {
    const isVisible = isOpen && hasAnimated && !isClosing;
    if (isVisible) {
      return 'translateX(0)';
    }
    // Slide off-screen in the direction of the panel position
    return position === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
  };

  // Only set default positioning if panelClassName doesn't override it
  const positionClass = position === 'right' 
    ? 'right-0' 
    : 'left-0';

  // Don't render anything if never opened and not opening/closing
  if (!isOpen && !hasAnimated && !isClosing) {
    return null;
  }

  return (
    <>
      {/* Enhanced Backdrop */}
      {showBackdrop && (
        <div
          className={cn(
            "fixed inset-y-0 right-0 left-14 md:left-56 transition-all duration-300",
            backdropVisible && isOpen
              ? 'bg-black/5'
              : 'bg-transparent',
            !isOpen && 'pointer-events-none',
            backdropClassName
          )}
          style={{
            top: topOffset,
            transition: 'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: zIndex - 10
          }}
          onClick={closeOnBackdropClick ? handleClose : undefined}
        />
      )}
      
      {/* Enhanced Slide-out Panel */}
      <div
        className={cn(
          `fixed inset-y-0 bg-background`,
          widthClasses[width],
          // Don't apply positionClass if panelClassName overrides positioning
          !panelClassName?.includes('left-[') && !panelClassName?.includes('right-[') && positionClass,
          !isOpen && 'pointer-events-none',
          'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]',
          panelClassName
        )}
        style={{
          top: topOffset,
          transform: getTransform(),
          transition: isOpen
            ? 'transform 500ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 400ms ease-out'
            : 'transform 350ms cubic-bezier(0.4, 0, 1, 1), box-shadow 300ms ease-in',
          zIndex: zIndex,
          maxWidth: '100vw',
          willChange: 'transform'
        }}
      >
        {/* Enhanced Edge Close Button */}
        {showEdgeCloseButton && (
          <button
            onClick={handleClose}
            className={cn(
              "absolute top-[1.9rem] h-8 w-8 rounded-full bg-primary hover:bg-primary-hover flex items-center justify-center z-[190] transition-all duration-300 shadow-xl drop-shadow-lg",
              "hover:scale-125 hover:shadow-2xl hover:rotate-90",
              showCloseBtn
                ? '-left-4 opacity-100 translate-x-0 scale-100'
                : '-left-8 opacity-0 translate-x-2 scale-75'
            )}
            aria-label="Close panel"
            style={{
              transition: showCloseBtn
                ? 'all 400ms cubic-bezier(0.16, 1, 0.3, 1)'
                : 'all 200ms cubic-bezier(0.4, 0, 1, 1)',
              boxShadow: showCloseBtn
                ? '0 8px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
          >
            <X className={cn(
              "h-4 w-4 text-primary-foreground transition-all duration-200",
              showCloseBtn ? "rotate-0" : "rotate-45"
            )} />
          </button>
        )}
        <div className={cn(
          "flex flex-col h-full overflow-hidden rounded-tl-lg transition-all duration-500",
          contentVisible 
            ? 'opacity-100 translate-y-0 scale-100' 
            : 'opacity-0 translate-y-2 scale-[0.99]'
        )}
        style={{
          transition: contentVisible
            ? 'opacity 400ms cubic-bezier(0.16, 1, 0.3, 1) 100ms, transform 400ms cubic-bezier(0.16, 1, 0.3, 1) 100ms'
            : 'opacity 200ms cubic-bezier(0.4, 0, 1, 1), transform 200ms cubic-bezier(0.4, 0, 1, 1)'
        }}>
          {/* Enhanced Header */}
          {(title || headerContent || showCloseButton) && (
            <div className={cn(
              "flex items-center justify-between p-6 border-b transition-all duration-300",
              contentVisible 
                ? "opacity-100 translate-y-0" 
                : "opacity-0 -translate-y-1",
              headerClassName
            )}
            style={{
              transition: contentVisible
                ? 'opacity 300ms cubic-bezier(0.16, 1, 0.3, 1) 150ms, transform 300ms cubic-bezier(0.16, 1, 0.3, 1) 150ms'
                : 'opacity 150ms cubic-bezier(0.4, 0, 1, 1), transform 150ms cubic-bezier(0.4, 0, 1, 1)'
            }}>
              {headerContent || (
                <h2 className="text-lg font-semibold text-foreground">{title}</h2>
              )}
              {showCloseButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="h-8 w-8 p-0 hover:bg-muted hover:scale-110 transition-all duration-200"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
          
          {/* Enhanced Content */}
          <div className={cn(
            "flex-1 overflow-y-auto p-6 transition-all duration-400",
            contentVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-1",
            contentClassName
          )}
          style={{
            transition: contentVisible
              ? 'opacity 350ms cubic-bezier(0.16, 1, 0.3, 1) 200ms, transform 350ms cubic-bezier(0.16, 1, 0.3, 1) 200ms'
              : 'opacity 150ms cubic-bezier(0.4, 0, 1, 1), transform 150ms cubic-bezier(0.4, 0, 1, 1)'
          }}>
            {children}
          </div>
          
          {/* Enhanced Footer */}
          {footer && (
            <div className={cn(
              "border-t p-6 transition-all duration-300",
              contentVisible 
                ? "opacity-100 translate-y-0" 
                : "opacity-0 translate-y-1",
              footerClassName
            )}
            style={{
              transition: contentVisible
                ? 'opacity 300ms cubic-bezier(0.16, 1, 0.3, 1) 250ms, transform 300ms cubic-bezier(0.16, 1, 0.3, 1) 250ms'
                : 'opacity 150ms cubic-bezier(0.4, 0, 1, 1), transform 150ms cubic-bezier(0.4, 0, 1, 1)'
            }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}